import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#f5f5f0]">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center text-red-600 hover:text-red-700 mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-gray-600 mb-8">Last updated: 18th January 2026</p>
        
        <div className="prose prose-gray max-w-none">
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 mb-4">
              30 Day Skills ("we", "our", "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website 30dayskills.co.uk (the "Site"), sign up for our email series, make purchases, or use our services.
            </p>
            <p className="text-gray-700 mb-4">
              We comply with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018. Please read this policy carefully. If you do not agree with the terms of this privacy policy, please do not access the Site.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-lg font-medium text-gray-900 mb-3">Information You Provide</h3>
            <p className="text-gray-700 mb-4">We collect information you voluntarily provide, including:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li><strong>Contact Information:</strong> Name, email address when you sign up for our email series or create an account.</li>
              <li><strong>Purchase Information:</strong> Payment details, billing address, and transaction history when you make a purchase. Payment card details are processed by our payment provider (Stripe) and are not stored on our servers.</li>
              <li><strong>Communications:</strong> Any information you provide when you contact us or respond to surveys.</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-900 mb-3">Information Collected Automatically</h3>
            <p className="text-gray-700 mb-4">When you visit our Site, we may automatically collect:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li><strong>Device Information:</strong> Browser type, operating system, device type.</li>
              <li><strong>Usage Data:</strong> Pages viewed, time spent on pages, links clicked, referring website.</li>
              <li><strong>IP Address:</strong> Your internet protocol address (may be anonymised).</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700 mb-4">We use your information for the following purposes:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li><strong>To Provide Services:</strong> Process purchases, deliver digital products, and manage your account.</li>
              <li><strong>To Communicate:</strong> Send you the 7-day email series you signed up for, transaction confirmations, and respond to inquiries.</li>
              <li><strong>To Send Marketing:</strong> With your consent, send occasional emails about new products, offers, or content. You can unsubscribe at any time.</li>
              <li><strong>To Improve Our Site:</strong> Analyse usage patterns to enhance user experience and develop new features.</li>
              <li><strong>To Ensure Security:</strong> Detect and prevent fraud, abuse, and security issues.</li>
              <li><strong>To Comply with Law:</strong> Meet legal obligations and respond to lawful requests.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Legal Basis for Processing (UK GDPR)</h2>
            <p className="text-gray-700 mb-4">We process your personal data on the following legal bases:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li><strong>Contract:</strong> Processing necessary to perform a contract with you (e.g., delivering purchased products).</li>
              <li><strong>Consent:</strong> Where you have given clear consent (e.g., subscribing to marketing emails).</li>
              <li><strong>Legitimate Interests:</strong> Where processing is necessary for our legitimate interests and does not override your rights (e.g., improving our services, preventing fraud).</li>
              <li><strong>Legal Obligation:</strong> Where we need to comply with a legal requirement.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Data Processors & Third Parties</h2>
            <p className="text-gray-700 mb-4">We share your information with the following third-party service providers who process data on our behalf:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li><strong>Stripe:</strong> Payment processing (USA, EU) — processes payment card data securely.</li>
              <li><strong>Postmark:</strong> Email delivery service (USA) — sends transactional and marketing emails.</li>
              <li><strong>Analytics Providers:</strong> Website analytics to understand usage patterns.</li>
            </ul>
            <p className="text-gray-700 mb-4">
              These providers are contractually obligated to protect your data and may only use it as directed by us. Where data is transferred outside the UK, appropriate safeguards are in place (e.g., Standard Contractual Clauses).
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Data Retention</h2>
            <p className="text-gray-700 mb-4">We retain your personal data only as long as necessary:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li><strong>Purchase Records:</strong> 7 years (for tax and legal compliance).</li>
              <li><strong>Email Subscribers:</strong> Until you unsubscribe, plus a short period to process the request.</li>
              <li><strong>Analytics Data:</strong> Typically 26 months, often in anonymised form.</li>
              <li><strong>Account Data:</strong> Until you request deletion or the account is inactive for an extended period.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Cookies</h2>
            <p className="text-gray-700 mb-4">
              Our Site uses cookies and similar tracking technologies. Cookies are small text files stored on your device that help us provide and improve our services.
            </p>
            <p className="text-gray-700 mb-4">We use:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li><strong>Essential Cookies:</strong> Necessary for the Site to function (e.g., session management).</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors use the Site.</li>
              <li><strong>Functional Cookies:</strong> Remember your preferences.</li>
            </ul>
            <p className="text-gray-700 mb-4">
              You can control cookies through your browser settings. Disabling certain cookies may affect Site functionality.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Your Rights</h2>
            <p className="text-gray-700 mb-4">Under UK GDPR, you have the following rights:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li><strong>Access:</strong> Request a copy of your personal data.</li>
              <li><strong>Rectification:</strong> Request correction of inaccurate data.</li>
              <li><strong>Erasure:</strong> Request deletion of your data ("right to be forgotten").</li>
              <li><strong>Restriction:</strong> Request limitation of processing.</li>
              <li><strong>Portability:</strong> Request transfer of your data in a machine-readable format.</li>
              <li><strong>Objection:</strong> Object to processing based on legitimate interests or for marketing.</li>
              <li><strong>Withdraw Consent:</strong> Where processing is based on consent, withdraw it at any time.</li>
            </ul>
            <p className="text-gray-700 mb-4">
              To exercise these rights, contact us at privacy@30dayskills.co.uk. We will respond within one month. You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) at ico.org.uk.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Security</h2>
            <p className="text-gray-700 mb-4">
              We implement appropriate technical and organisational measures to protect your personal data against unauthorised access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Children's Privacy</h2>
            <p className="text-gray-700 mb-4">
              Our Site and services are not intended for individuals under the age of 18. We do not knowingly collect personal data from children. If you believe we have collected data from a child, please contact us immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Changes to This Policy</h2>
            <p className="text-gray-700 mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date. We encourage you to review this policy periodically.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <p className="text-gray-700">
              <strong>Email:</strong> privacy@30dayskills.co.uk<br />
              <strong>Website:</strong> 30dayskills.co.uk
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
